import { FormEvent, useEffect, useMemo, useState } from 'react';
import { TableIconButton } from '../../components/TableIconAction';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Button,
  Callout,
  Label,
  ListEmptyState,
  LoadingSpinner,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  PlusIcon,
  SaveIcon,
  Switch,
  TextInput,
  Textarea,
  XIcon,
} from '../../components/ui';
import { BanIcon, PencilIcon } from '../../components/ui/Icons';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { ExpenseCategory } from '../../types/shipment';

const categorySortGetters: Record<
  string,
  (item: ExpenseCategory) => string | number | boolean | null | undefined
> = {
  name: (c) => c.name,
  description: (c) => c.description,
  active: (c) => c.active,
};

export function ExpenseCategoriesPage() {
  const labels = t().expenseCategories;
  const [items, setItems] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const { sort, toggleSort } = useTableSort('name');
  const displayItems = useMemo(
    () => sortItems(items, sort, categorySortGetters),
    [items, sort],
  );
  const [listError, setListError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    setListError(null);
    try {
      setItems(await api.getExpenseCategories(false));
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function closeForm() {
    setFormOpen(false);
    setEditingId(null);
    setName('');
    setDescription('');
    setActive(true);
    setFormError(null);
  }

  function openCreate() {
    setEditingId(null);
    setName('');
    setDescription('');
    setActive(true);
    setFormError(null);
    setMessage(null);
    setFormOpen(true);
  }

  function openEdit(category: ExpenseCategory) {
    setEditingId(category.id);
    setName(category.name);
    setDescription(category.description ?? '');
    setActive(category.active);
    setFormError(null);
    setMessage(null);
    setFormOpen(true);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setMessage(null);

    if (!name.trim()) {
      setFormError(labels.requiredName);
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.updateExpenseCategory(editingId, {
          name: name.trim(),
          description: description.trim() || undefined,
          active,
        });
      } else {
        await api.createExpenseCategory({
          name: name.trim(),
          description: description.trim() || undefined,
          active,
        });
      }
      setMessage(labels.saveSuccess);
      closeForm();
      await load();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : t().toasts.saveError,
      );
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(id: number) {
    setListError(null);
    setMessage(null);
    try {
      await api.deactivateExpenseCategory(id);
      await load();
    } catch (err) {
      setListError(
        err instanceof ApiError ? err.message : t().toasts.saveError,
      );
    }
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <Button type="button" size="sm" onClick={openCreate}>
            <PlusIcon className="h-3.5 w-3.5" />
            {labels.new}
          </Button>
        }
      />

      {listError ? (
        <Callout variant="danger" className="shrink-0">
          {listError}
        </Callout>
      ) : null}
      {message ? (
        <Callout variant="success" className="shrink-0">
          {message}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : displayItems.length === 0 ? (
          <ListEmptyState
            title={labels.emptyTitle}
            hint={labels.emptyHint}
          />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="whitespace-nowrap px-3 py-2 font-medium">
                  {t().common.actions}
                </th>
                <SortableTh sortKey="name" sort={sort} onSort={toggleSort}>
                  {labels.name}
                </SortableTh>
                <SortableTh
                  sortKey="description"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {labels.description}
                </SortableTh>
                <SortableTh sortKey="active" sort={sort} onSort={toggleSort}>
                  {t().customers.status}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((category) => (
                <tr key={category.id} className="border-t border-line">
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="inline-flex items-center gap-0.5">
                      <TableIconButton
                        label="Επεξεργασία"
                        onClick={() => openEdit(category)}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </TableIconButton>
                      {category.active ? (
                        <TableIconButton
                          label={labels.deactivate}
                          variant="muted"
                          onClick={() => void deactivate(category.id)}
                        >
                          <BanIcon className="h-4 w-4" />
                        </TableIconButton>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-medium">{category.name}</td>
                  <td className="px-3 py-2">{category.description || '—'}</td>
                  <td className="px-3 py-2">
                    {category.active ? labels.active : labels.inactive}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTablePanel>

      <Modal isOpen={formOpen} onClose={closeForm} size="md">
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <ModalHeader>
            <ModalTitle>
              {editingId ? labels.edit : labels.new}
            </ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={closeForm}
              aria-label={t().common.cancel}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </ModalHeader>
          <ModalContent className="space-y-4">
            {formError ? <Callout variant="danger">{formError}</Callout> : null}
            <div>
              <Label required>{labels.name}</Label>
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                showClearButton={false}
                placeholder=""
                autoFocus
              />
            </div>
            <div>
              <Label>{labels.description}</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
            <Switch
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              label={labels.active}
            />
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={closeForm}>
              <XIcon className="h-4 w-4" />
              {t().common.cancel}
            </Button>
            <Button type="submit" loading={saving}>
              <SaveIcon className="h-4 w-4" />
              {t().common.save}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </PageShell>
  );
}
